import { Controller, useForm } from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { ManagementApiUrl } from "environment/routeSettings";
import { netCall } from "utils/netCall";
import { useEffect, useState } from "react";
import {
  TWaterMeter,
  WaterMeterAuthType,
  WaterMeterCommunicationType,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";
import { toast } from "react-toastify";
import { Select } from "components/Select";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { TransparentLoading } from "components/TransparentLoading";
import { TResponseCode } from "utils/netCall/types";

type TProps = {
  waterMeterId?: string;
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (value: TEditFormTypes) => void;
  isLoading: boolean;
};

export type TEditFormTypes = {
  serialNo: string;
  productionYear: number;
  waterMeterType: WaterMeterType;
  locationType: WaterMeterLocationType;
  modelType: WaterMeterModelType;
  provinceId?: string;
  cityId?: string;
  jurisdictionId?: string;
  config: {
    communicationType: WaterMeterCommunicationType;
    authType: WaterMeterAuthType;
    encryptionkey: string;
    // Only for OTTA
    devEUI: string;
    appEUI: string;
    loraAppKey: string;
    // Only for ABP
    loraAppSKey: string;
    loraDeviceAddress: string;
    loraNetworkKey: string;
  };
};

const waterMeterAuthTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterAuthType),
  "name"
);
const waterMeterTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterType),
  "name"
);
const waterMeterLocationTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterLocationType),
  "name"
).filter(
  (item) => String(item.value) !== String(WaterMeterLocationType.Installed)
);
const waterMeterModelTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterModelType),
  "name"
);

export function EditMeterModal(props: TProps) {
  const { waterMeterId, isOpen = false, onClose, onSubmit, isLoading } = props;

  const { t } = useTranslation();

  const [isGettingData, setIsGettingData] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TEditFormTypes>({
    defaultValues: undefined,
  });

  const getMeterDetails = async (waterMeterId: string) => {
    setIsGettingData(true);
    const res = await netCall<TWaterMeter>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterMeter/GetWaterMeter`,
      body: {
        waterMeterId: waterMeterId,
        startDT: new Date().toISOString().split("T")[0],
        endDT: new Date().toISOString().split("T")[0],
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      const record = res.results;
      setValue("serialNo", record?.serialNo);
      setValue("productionYear", record?.productionYear);
      setValue("waterMeterType", record?.waterMeterType);
      setValue("locationType", record?.locationType);
      setValue("modelType", record?.modelType);
      setValue("provinceId", record?.provinceId);
      setValue("cityId", record?.cityId);
      setValue("jurisdictionId", record?.jurisdictionId);
      setValue("config.authType", record?.config.authType);
      setValue("config.encryptionkey", record?.config.encryptionkey);
      setValue("config.devEUI", record?.config.devEUI);
      setValue("config.appEUI", record?.config.appEUI);
      setValue("config.loraAppKey", record?.config.loraAppKey);
      setValue("config.loraAppSKey", record?.config.loraAppSKey);
      setValue("config.loraDeviceAddress", record?.config.loraDeviceAddress);
      setValue("config.loraNetworkKey", record?.config.loraNetworkKey);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  useEffect(() => {
    if (isOpen && waterMeterId) {
      getMeterDetails(waterMeterId);
    }
  }, [isOpen, waterMeterId]);

  const renderDifferentConfigByAuthType = () => {
    const isABP = watch("config.authType") === WaterMeterAuthType.ABP;
    if (!isABP) {
      return (
        <>
          <Controller
            name={"config.devEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("devEUI")}
                  placeholder={t("devEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
          <Controller
            name={"config.appEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("appEUI")}
                  placeholder={t("appEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
          <Controller
            name={"config.loraAppKey"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppKey")}
                  placeholder={t("loraAppKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
        </>
      );
    } else {
      return (
        <>
          <Controller
            name={"config.loraAppSKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppSKey")}
                  placeholder={t("loraAppSKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
          <Controller
            name={"config.loraDeviceAddress"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraDeviceAddress")}
                  placeholder={t("loraDeviceAddress")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
          <Controller
            name={"config.loraNetworkKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraNetworkKey")}
                  placeholder={t("loraNetworkKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                />
              );
            }}
          />
        </>
      );
    }
  };

  return (
    <Dialog
      maxWidth={false}
      dialogTitle={t("editMeter")}
      open={isOpen}
      onClose={onClose}
      canClose={!isLoading}
    >
      <form className="p-4 w-[640px]" onSubmit={handleSubmit(onSubmit)}>
        {isGettingData && <TransparentLoading />}
        <div className="flex flex-col w-full gap-y-3 px-2">
          <div className={"flex gap-x-1"}>
            <p className={"font-bold text-primary-80"}>
              {t("waterMeterModelType")}:
            </p>
            <p>{WaterMeterModelType[watch("modelType")]}</p>
            <p className={"text-tiny"}>({t("unEditable")})</p>
          </div>
          <p className={"font-bold text-primary-80"}>{t("meterInfo")}</p>
          <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
            <Controller
              name={"serialNo"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("serialNo")}
                    placeholder={t("serialNo")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"col-span-2"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                  />
                );
              }}
            />
            <Controller
              name={"productionYear"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("productionYear")}
                    type={"number"}
                    placeholder={t("productionYear")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"col-span-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                  />
                );
              }}
            />
            <Controller
              name={"waterMeterType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="col-span-1"
                    label={t("waterMeterType")}
                    options={waterMeterTypeOptions}
                    initialValue={waterMeterTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterType) ===
                        field.value
                    )}
                    isError={!!error}
                  />
                );
              }}
            />
          </div>
          <p className={"font-bold text-primary-80 mt-3"}>
            {t("meterLocationInfo")}
          </p>
          <div className={"grid grid-cols-3 gap-x-2 gap-y-3"}>
            <div className={"col-span-3"}>
              <RegionAutoComplete
                defaultValue={{
                  provinceId: getValues("provinceId"),
                  cityId: getValues("cityId"),
                  jurisdictionId: getValues("jurisdictionId"),
                }}
                onChange={(value) => {
                  setValue("provinceId", value?.province?.id);
                  setValue("cityId", value?.city?.id);
                  setValue("jurisdictionId", value?.jurisdiction?.id);
                }}
              />
            </div>
            <Controller
              name={"locationType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="col-span-1"
                    label={t("waterMeterLocationType")}
                    options={waterMeterLocationTypeOptions}
                    initialValue={waterMeterLocationTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterLocationType) ===
                        field.value
                    )}
                    isError={!!error}
                  />
                );
              }}
            />
          </div>
          <p className={"font-bold text-primary-80 mt-3"}>
            {t("meterCommunicationInfo")}
          </p>
          <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
            <Controller
              name={"config.authType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    size="small"
                    className="col-span-1"
                    label={t("waterMeterAuthType")}
                    options={waterMeterAuthTypeOptions}
                    onChange={(e) => {
                      field.onChange(e?.value);
                      if (field.value !== e?.value) {
                        setValue("config.devEUI", "");
                        setValue("config.appEUI", "");
                        setValue("config.loraAppKey", "");
                        setValue("config.loraAppSKey", "");
                        setValue("config.loraDeviceAddress", "");
                        setValue("config.loraNetworkKey", "");
                      }
                    }}
                    // value={field.value}
                    initialValue={waterMeterAuthTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterAuthType) ==
                        field.value
                    )}
                  />
                );
              }}
            />
            <Controller
              name={"config.encryptionkey"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("encryptionkey")}
                    placeholder={t("encryptionkey")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className="col-span-2"
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                  />
                );
              }}
            />
            {renderDifferentConfigByAuthType()}
          </div>
        </div>
        <div className="col-span-12 flex items-center justify-end mt-5">
          <Button
            className="w-20"
            loading={isLoading}
            type="submit"
            variant="contained"
          >
            {t("edit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
